import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContactForm from "@/pages/about/components/ContactForm";

const services = [
  { icon: "ri-book-2-line", title: "海量书库", desc: "覆盖玄幻、言情、历史等热门分类，持续收录优质作品。" },
  { icon: "ri-filter-3-line", title: "智能筛选", desc: "按分类、标签与热度快速筛选，几步就能找到想看的书。" },
  { icon: "ri-search-line", title: "快捷搜索", desc: "支持书名、作者与分类关键词搜索，结果清晰直观。" },
  { icon: "ri-book-open-line", title: "舒适阅读", desc: "章节排版清爽，字号与阅读背景随心切换，久看不累。" },
];

const guides = [
  "在顶部导航或首页搜索框输入关键词，即可找到想读的小说。",
  "进入小说详情页，可查看简介、章节目录与相关推荐。",
  "点击任意章节即可开始阅读，阅读页支持上一章 / 下一章连续阅读。",
  "阅读设置中可调整字体大小与阅读背景，设置会自动保存在本地浏览器。",
  "点击收藏按钮即可把喜欢的小说加入收藏，无需登录。",
];

export default function AboutPage() {
  useDocumentMeta(
    "关于我们 - 御书屋",
    "了解御书屋的定位、服务内容与联系方式。御书屋致力于为网络小说爱好者提供清爽舒适的在线阅读体验。",
  );

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mb-5">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="mb-8 overflow-hidden rounded-lg bg-primary-950 px-6 py-10 md:px-10 md:py-14">
        <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
          关于御书屋
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
          御书屋是一个专注网络小说的在线阅读与推荐平台，面向喜爱玄幻、言情、历史等题材的广大读者。
          我们希望把复杂的找书与阅读流程做得足够简单：清晰的书库、顺手的筛选、舒适的阅读排版，
          让每一次打开都值得。
        </p>
        <div className="mt-6 flex flex-wrap gap-6">
          <div>
            <p className="font-heading text-2xl font-bold text-accent-400">30+</p>
            <p className="mt-1 text-xs text-background-300">收录作品</p>
          </div>
          <div>
            <p className="font-heading text-2xl font-bold text-accent-400">8</p>
            <p className="mt-1 text-xs text-background-300">热门分类</p>
          </div>
          <div>
            <p className="font-heading text-2xl font-bold text-accent-400">24/7</p>
            <p className="mt-1 text-xs text-background-300">随时阅读</p>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">我们提供什么</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((service) => (
            <div
              key={service.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${service.icon} text-2xl`}></i>
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                {service.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">使用说明</h2>
            <ol className="flex flex-col gap-3">
              {guides.map((guide, i) => (
                <li key={guide} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700">
                    {i + 1}
                  </span>
                  {guide}
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">联系方式</h2>
            <ul className="flex flex-col gap-4 text-sm">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-foreground-400">联系邮箱</span>
                  <span className="font-medium text-foreground-900">support@yushuwu.example.com</span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-foreground-400">服务时间</span>
                  <span className="font-medium text-foreground-900">周一至周五 09:00 - 18:00</span>
                </span>
              </li>
            </ul>
            <p className="mt-4 rounded-md bg-secondary-50 px-4 py-3 text-xs leading-relaxed text-secondary-900">
              本站为阅读体验演示站点，不含用户注册、在线支付与文件下载功能，页面中的互动均为前端演示。
            </p>
          </section>
        </div>

        <section>
          <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">意见反馈</h2>
          <ContactForm />
        </section>
      </div>

      <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">版权声明</h2>
        <p className="text-sm leading-relaxed text-foreground-600">
          本站所有小说信息、简介与章节内容均为演示用途生成的示例文本，不对应任何真实出版物或网络作品。
          御书屋提倡正版阅读，尊重每一位创作者的劳动成果。若本站内容无意中侵犯了您的权益，请通过上方联系方式与我们取得联系，我们会及时处理。
        </p>
      </section>
    </div>
  );
}